import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import BannerStack from "@/components/BannerStack";
import ShareSheet from "@/components/ShareSheet";
import EventCreateModal from "@/components/EventCreateModal";
import Avatar from "@/components/Avatar";
import InlineText from "@/components/InlineText";
import FollowButton from "@/components/FollowButton";
import GoLiveButton from "@/components/GoLiveButton";
import { useAuth } from "@/contexts/AuthContext";
import { EVENT_COLORS } from "@/lib/eventColors";
import {
  ChevronLeft, MapPin, Sparkles, Plus, Trash2,
  Wallet, HeartHandshake, Loader2, Check, Heart, Map as MapIcon, X, Star,
  PartyPopper, Baby, ShoppingBag, ExternalLink, Truck, EyeOff, Eye,
  Share2, Pencil,
} from "lucide-react";

const KINDS = [
  { id: "likes", title: "Things they like", listKey: "likes", emoji: <Heart size={14} /> },
  { id: "places", title: "Places to go", listKey: "places", emoji: <MapIcon size={14} /> },
  { id: "todos_before", title: "Before the event", listKey: "todos_before", emoji: <Check size={14} /> },
  { id: "todos_during", title: "During the event", listKey: "todos_during", emoji: <Check size={14} /> },
  { id: "todos_after", title: "After the event", listKey: "todos_after", emoji: <Check size={14} /> },
];

export default function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const deleteEvent = async () => {
    if (!event) return;
    const label = event.title || "this event";
    if (!window.confirm(`Delete "${label}"? This removes the event and all its data (expenses, registry, bring list, attention levels, excitement, reminders). This can't be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/events/${event.event_id}`);
      navigate("/app");
    } catch (e) {
      setErr(formatError(e));
      setDeleting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/events/${eventId}`);
      setEvent(data);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [eventId]);

  const isCreator = event && user && event.created_by === user.user_id;

  const toggleHighlight = async () => {
    try {
      const { data } = await api.patch(`/events/${eventId}`, { is_highlighted: !event.is_highlighted });
      setEvent({ ...event, ...data });
    } catch (e) { setErr(formatError(e)); }
  };

  const addItem = async (key, value) => {
    if (!value.trim()) return;
    const next = [...(event[key] || []), value.trim()];
    try {
      const { data } = await api.patch(`/events/${eventId}`, { [key]: next });
      setEvent({ ...event, ...data });
    } catch (e) { setErr(formatError(e)); }
  };

  const removeItem = async (key, idx) => {
    const next = [...(event[key] || [])];
    next.splice(idx, 1);
    try {
      const { data } = await api.patch(`/events/${eventId}`, { [key]: next });
      setEvent({ ...event, ...data });
    } catch (e) { setErr(formatError(e)); }
  };

  const totalExpenses = useMemo(
    () => (event?.expenses || []).reduce((a, b) => a + (b.amount || 0), 0),
    [event]
  );

  if (loading) {
    return <AppShell><div className="grid place-items-center py-20"><Loader2 className="animate-spin" /></div></AppShell>;
  }
  if (!event) {
    return (
      <AppShell>
        <div className="bd-surface p-6">
          <p style={{ color: "#ff3b30" }}>{err || "Event not found."}</p>
          <Link to="/app" className="bd-btn mt-3 inline-flex"><ChevronLeft size={16} /> Back</Link>
        </div>
      </AppShell>
    );
  }

  const color = EVENT_COLORS[event.event_type] || EVENT_COLORS.general;
  const dateObj = new Date(event.date + "T00:00:00");
  const babyTitle = event.event_type === "baby"
    ? (event.is_we_having ? "We're having a baby" : "I'm having a baby")
    : null;

  return (
    <AppShell>
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button onClick={() => navigate(-1)} className="bd-btn bd-btn-ghost" data-testid="event-back">
            <ChevronLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-2">
            {isCreator && (
              <>
                <button
                  onClick={() => setShowEdit(true)}
                  className="bd-btn bd-btn-ghost"
                  style={{ padding: "0.55rem 0.9rem", fontSize: "0.85rem", fontWeight: 600 }}
                  data-testid="event-edit-btn"
                  title="Edit event details — date, time, title, etc."
                >
                  <Pencil size={14} /> Edit details
                </button>
                <button
                  onClick={deleteEvent}
                  disabled={deleting}
                  className="bd-btn bd-btn-ghost"
                  style={{
                    padding: "0.55rem 0.9rem",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: "#ff3b30",
                  }}
                  data-testid="event-delete-btn"
                  title="Delete this event permanently"
                >
                  {deleting ? <Loader2 size={14} className="animate-spin" /> : <><Trash2 size={14} /> Delete</>}
                </button>
              </>
            )}
            <button
              onClick={() => setShowShare(true)}
              className="bd-btn"
              style={{
                padding: "0.7rem 1.2rem",
                fontSize: "0.95rem",
                fontWeight: 700,
                letterSpacing: "-0.01em",
                boxShadow: "0 4px 14px rgba(255, 122, 89, 0.28)",
              }}
              data-testid="event-invite-btn"
            >
              <Share2 size={16} /> Invite to this event
            </button>
            <GoLiveButton
              scope="event"
              targetId={event.event_id}
              label={event.event_type === "workout" ? "Start Workout" : "Go Live"}
              variant="primary"
              testId="event-go-live-btn"
              title="Go live on this event — everyone in the group gets a Join In alert"
              style={{ padding: "0.7rem 1.2rem", fontSize: "0.95rem" }}
            />
          </div>
        </div>

        {/* Banners for this event (global + group + event, respecting disable toggles) */}
        <BannerStack
          eventId={event.event_id}
          scopeForAdd={isCreator ? `event:${event.event_id}` : null}
          canManage={isCreator}
        />

        {isCreator && (
          <button
            className="bd-btn bd-btn-ghost self-start"
            style={{ fontSize: "0.75rem", padding: "0.3rem 0.7rem" }}
            onClick={async () => {
              const next = !event.disable_inherited_banners;
              try {
                const { data } = await api.patch(`/events/${event.event_id}`, { disable_inherited_banners: next });
                setEvent({ ...event, ...data });
              } catch (e) { setErr(formatError(e)); }
            }}
            data-testid="event-disable-inherited-banners"
          >
            {event.disable_inherited_banners
              ? <><Eye size={11} /> Show global &amp; group banners here</>
              : <><EyeOff size={11} /> Hide global &amp; group banners on this event</>}
          </button>
        )}

        <div className="bd-surface p-6 sm:p-8" data-testid="event-hero">
          <div className="flex items-start gap-4 flex-wrap">
            <span
              className="bd-chip uppercase font-bold"
              style={{ background: color, color: "#000", borderColor: color }}
            >
              {event.event_type.replace(/_/g, " ")}
            </span>
            <div className="bd-chip bd-mono" data-testid="event-date-chip">
              {dateObj.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              {event.time ? ` · ${event.time}` : ""}
            </div>
            {event.location && (
              <div className="bd-chip"><MapPin size={12} /> {event.location}</div>
            )}
            {event.is_highlighted && (
              <div className="bd-chip" style={{ background: "var(--primary)", color: "var(--primary-fg)", borderColor: "var(--primary)" }}>
                <Star size={10} fill="currentColor" /> Highlighted
              </div>
            )}
            {isCreator && (
              <button
                onClick={toggleHighlight}
                className="bd-btn bd-btn-ghost"
                style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem" }}
                data-testid="event-highlight-toggle"
              >
                <Star size={12} fill={event.is_highlighted ? "currentColor" : "none"} />
                {event.is_highlighted ? "Remove highlight" : "Highlight this event"}
              </button>
            )}
          </div>
          {babyTitle && (
            <p className="mt-4 text-lg font-semibold" style={{ color: "var(--text-dim)" }}>{babyTitle}</p>
          )}
          <h1 className="text-4xl sm:text-5xl font-extrabold mt-3" style={{ letterSpacing: "-0.04em" }} data-testid="event-title-h1">
            <InlineText
              value={event.title}
              readOnly={!isCreator}
              testId="event-title-inline"
              ariaLabel="Edit event title"
              maxLength={120}
              onSave={async (next) => {
                await api.patch(`/events/${event.event_id}`, { title: next });
                await load();
              }}
            />
          </h1>
          {event.person_name && (
            <p className="mt-2 text-lg" style={{ color: "var(--text-dim)" }}>
              For <strong style={{ color: "var(--text)" }}>{event.person_name}</strong>
            </p>
          )}
          {event.description && (
            <p className="mt-3 max-w-2xl" style={{ color: "var(--text-dim)" }}>{event.description}</p>
          )}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <FollowButton
              targetType="event"
              targetId={event.event_id}
              label="Follow & alert me"
              testId="event-follow"
            />
            <span className="text-xs" style={{ color: "var(--text-dim)" }}>
              Get notified when this event is near.
            </span>
          </div>
        </div>

        {err && <div className="text-sm" style={{ color: "#ff3b30" }} data-testid="event-error">{err}</div>}

        {/* Bring List (for hosted events) */}
        <BringList event={event} onChanged={load} />

        {/* Baby registry */}
        {event.event_type === "baby" && (
          <BabyRegistry event={event} onChanged={load} isCreator={isCreator} />
        )}

        {/* Fundraiser */}
        {(event.event_type === "fundraiser" || event.fundraiser_goal) && (
          <FundraiserBlock event={event} onRefresh={load} />
        )}

        {/* Rich Sub-data */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {KINDS.map((k) => (
            <SubList key={k.id} event={event} kind={k} onAdd={addItem} onRemove={removeItem} />
          ))}
        </div>

        {/* Map */}
        {event.location && <LocationBlock location={event.location} />}

        {/* Expenses */}
        <ExpenseBlock event={event} total={totalExpenses} onChanged={load} />
      </div>

      {showShare && (
        <ShareSheet
          eventId={event.event_id}
          inviterName={user?.name}
          onClose={() => setShowShare(false)}
        />
      )}

      {showEdit && (
        <EventCreateModal
          open={showEdit}
          onClose={() => setShowEdit(false)}
          groups={[]}
          editEvent={event}
          onCreated={() => { setShowEdit(false); load(); }}
        />
      )}
    </AppShell>
  );
}

// ---------------- subcomponents ---------------- //
function BringList({ event, onChanged }) {
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!label.trim()) return;
    setBusy(true);
    try {
      await api.post(`/events/${event.event_id}/bring`, { label, quantity: 1 });
      setLabel("");
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const toggleClaim = async (item) => {
    const claim = !item.claimed_by;
    await api.post(`/bring/${item.bring_id}/claim`, { claim });
    onChanged();
  };

  const remove = async (item) => {
    await api.delete(`/bring/${item.bring_id}`);
    onChanged();
  };

  const items = event.bring_items || [];

  return (
    <div className="bd-surface p-5" data-testid="bring-block">
      <div className="flex items-center gap-2 mb-4 font-bold text-lg">
        <ShoppingBag size={18} /> Who&apos;s bringing what
      </div>
      <ul className="space-y-2 mb-3">
        {items.length === 0 && (
          <li className="text-sm" style={{ color: "var(--text-dim)" }}>
            Add items the group needs — chips, drinks, ice. Each person clicks to claim one.
          </li>
        )}
        {items.map((it) => (
          <li
            key={it.bring_id}
            className="flex items-center justify-between gap-2 px-3 py-2 rounded"
            style={{ border: "1px solid var(--border)", background: it.claimed_by ? "var(--bg-soft)" : "transparent" }}
            data-testid={`bring-${it.bring_id}`}
          >
            <span className="flex-1 truncate">{it.label}</span>
            {it.claimed_by_name && (
              <span className="bd-chip flex items-center gap-1.5" style={{ fontSize: "0.7rem" }}>
                <Avatar name={it.claimed_by_name} picture={it.claimed_by_picture} size={16} />
                {it.claimed_by_name}
              </span>
            )}
            <button
              onClick={() => toggleClaim(it)}
              className="bd-btn"
              style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem",
                background: it.claimed_by ? "var(--bg-soft)" : "var(--primary)",
                color: it.claimed_by ? "var(--text)" : "var(--primary-fg)" }}
              data-testid={`bring-claim-${it.bring_id}`}
            >
              {it.claimed_by ? "Release" : "I'll bring this"}
            </button>
            <button onClick={() => remove(it)} className="bd-btn bd-btn-ghost" style={{ padding: "0.3rem 0.45rem" }} data-testid={`bring-remove-${it.bring_id}`}>
              <Trash2 size={11} />
            </button>
          </li>
        ))}
      </ul>
      <form
        onSubmit={(e) => { e.preventDefault(); add(); }}
        className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2"
      >
        <input
          className="bd-input"
          placeholder="What's needed? (chips, drinks, ice…)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          data-testid="bring-input"
        />
        <button className="bd-btn" disabled={busy} type="submit" data-testid="bring-add">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <><Plus size={12} /> Add</>}
        </button>
      </form>
    </div>
  );
}

const REGISTRY_QUICK = [
  "Crib", "Car seat", "Stroller", "Bottles", "Diapers size 1",
  "Diapers size 2", "Baby monitor", "Swaddle blankets", "Pacifiers",
  "Bouncer", "High chair", "Baby bath tub", "Wipes", "Onesies 0-3 months", "Sleep sack",
];

function isAmazonUrl(u) {
  try {
    const h = new URL(u).hostname.toLowerCase();
    return h.endsWith("amazon.com") || h === "a.co" || h === "amzn.to" || h === "amzn.com";
  } catch {
    return false;
  }
}

function BabyRegistry({ event, onChanged, isCreator }) {
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const add = async (preset = null) => {
    const lab = preset || label;
    if (!lab.trim()) { setErr("Add an item name."); return; }
    if (!preset && url && !isAmazonUrl(url)) {
      setErr("Only Amazon links are accepted. Leave the link blank to search Amazon automatically.");
      return;
    }
    setBusy(true); setErr("");
    try {
      await api.post(`/events/${event.event_id}/registry`, {
        label: lab, url: preset ? "" : url, quantity: 1, notes: "",
      });
      setLabel(""); setUrl("");
      onChanged();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const claim = async (item) => {
    await api.post(`/registry/${item.registry_id}/claim`, { claim: !item.claimed_by });
    onChanged();
  };

  const remove = async (item) => {
    await api.delete(`/registry/${item.registry_id}`);
    onChanged();
  };

  const items = event.registry_items || [];

  return (
    <div className="bd-surface p-5" data-testid="registry-block">
      <div className="flex items-center gap-2 mb-2 font-bold text-lg">
        <Baby size={18} /> Baby registry
      </div>
      <p className="text-xs mb-4" style={{ color: "var(--text-dim)" }}>
        Powered by Amazon. All registry links go through the Born Day Amazon affiliate program.
      </p>

      <ul className="space-y-2 mb-4">
        {items.length === 0 && (
          <li className="text-sm" style={{ color: "var(--text-dim)" }}>
            Nothing on the registry yet. Use the quick-add chips below or paste an Amazon link.
          </li>
        )}
        {items.map((it) => (
          <RegistryRow
            key={it.registry_id}
            item={it}
            onClaim={() => claim(it)}
            onRemove={() => remove(it)}
            onChanged={onChanged}
            isCreator={isCreator}
          />
        ))}
      </ul>

      <div className="mb-3">
        <div className="bd-label">Quick-add common items</div>
        <div className="flex flex-wrap gap-1.5">
          {REGISTRY_QUICK.map((q) => (
            <button
              key={q}
              type="button"
              className="bd-chip"
              onClick={() => add(q)}
              disabled={busy}
              data-testid={`registry-quick-${q.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            >
              <Plus size={10} /> {q}
            </button>
          ))}
        </div>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); add(); }}
        className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2"
      >
        <input
          className="bd-input"
          placeholder="Item name (e.g. Diaper bag)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          data-testid="registry-label"
        />
        <input
          className="bd-input bd-mono"
          placeholder="Amazon link (or leave blank)"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          data-testid="registry-url"
        />
        <button className="bd-btn" disabled={busy} type="submit" data-testid="registry-add">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <><Plus size={12} /> Add</>}
        </button>
      </form>
      {err && (
        <div className="text-xs mt-2" style={{ color: "#ff3b30" }} data-testid="registry-error">{err}</div>
      )}
      <p className="text-xs mt-2" style={{ color: "var(--text-dim)" }}>
        Amazon-only. Paste any Amazon URL (amazon.com, a.co, amzn.to). Leave blank and we&apos;ll create an Amazon search link for you.
      </p>
    </div>
  );
}

function RegistryRow({ item, onClaim, onRemove, onChanged, isCreator }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tracking, setTracking] = useState(item.tracking_number || "");
  const [carrier, setCarrier] = useState(item.carrier || "");
  const [status, setStatus] = useState(item.status || "pending");
  const [busy, setBusy] = useState(false);
  const claimed = !!item.claimed_by;
  const canUpdate = claimed && (item.claimed_by === user?.user_id || isCreator);
  const ship = item.ship_to || {};

  const saveTracking = async () => {
    setBusy(true);
    try {
      await api.post(`/registry/${item.registry_id}/tracking`, {
        tracking_number: tracking,
        carrier,
        status,
      });
      onChanged();
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const STATUS_COLORS = {
    pending: "#9EA1D4",
    ordered: "#FFB300",
    shipped: "#00E5FF",
    delivered: "#7CD9A8",
    cancelled: "#FF3B30",
  };

  return (
    <li
      className="px-3 py-2 rounded"
      style={{ border: "1px solid var(--border)", background: claimed ? "var(--bg-soft)" : "transparent" }}
      data-testid={`registry-${item.registry_id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{item.label}</div>
          <a href={item.url} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1" style={{ color: "var(--text-dim)" }}>
            <ExternalLink size={10} /> View on Amazon
          </a>
        </div>
        {claimed && (
          <span className="bd-chip flex items-center gap-1.5" style={{ fontSize: "0.7rem",
            background: STATUS_COLORS[item.status] + "33",
            borderColor: STATUS_COLORS[item.status] }}>
            <Avatar name={item.claimed_by_name} picture={item.claimed_by_picture} size={16} />
            {item.claimed_by_name} · {item.status}
          </span>
        )}
        <button
          onClick={onClaim}
          className="bd-btn"
          style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem",
            background: claimed ? "var(--bg-soft)" : "var(--primary)",
            color: claimed ? "var(--text)" : "var(--primary-fg)" }}
          data-testid={`registry-claim-${item.registry_id}`}
        >
          {claimed ? "Release" : "I've got this"}
        </button>
        {canUpdate && (
          <button
            onClick={() => setOpen(!open)}
            className="bd-btn bd-btn-ghost"
            style={{ padding: "0.3rem 0.55rem", fontSize: "0.7rem" }}
            data-testid={`registry-tracking-btn-${item.registry_id}`}
          >
            <Truck size={11} /> Tracking
          </button>
        )}
        {isCreator && (
          <button onClick={onRemove} className="bd-btn bd-btn-ghost" style={{ padding: "0.3rem 0.45rem" }} data-testid={`registry-remove-${item.registry_id}`}>
            <Trash2 size={11} />
          </button>
        )}
      </div>

      {claimed && ship.shipping_street && (
        <div className="mt-2 text-xs flex items-center gap-1" style={{ color: "var(--text-dim)" }}>
          <Truck size={11} /> Ships to {ship.shipping_name || "the parent"} ·{" "}
          {ship.shipping_street}{ship.shipping_street2 ? `, ${ship.shipping_street2}` : ""}, {ship.shipping_city}, {ship.shipping_state} {ship.shipping_postal}
        </div>
      )}
      {claimed && !ship.shipping_street && (
        <div className="mt-2 text-xs" style={{ color: "#FFB300" }} data-testid={`registry-no-address-${item.registry_id}`}>
          ⚠ The parent-to-be hasn&apos;t set a shipping address yet — ask them to add one in their profile.
        </div>
      )}
      {item.tracking_number && (
        <div className="mt-1 text-xs bd-mono" style={{ color: "var(--text-dim)" }}>
          {item.carrier ? `${item.carrier} · ` : ""}#{item.tracking_number}
          {item.expected_delivery ? ` · expected ${item.expected_delivery}` : ""}
        </div>
      )}

      {open && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2" data-testid={`registry-tracking-form-${item.registry_id}`}>
          <input
            className="bd-input bd-mono"
            placeholder="Tracking #"
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
            data-testid={`registry-tracking-input-${item.registry_id}`}
          />
          <select className="bd-input" value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid={`registry-carrier-${item.registry_id}`}>
            <option value="">Carrier…</option>
            <option>Amazon</option>
            <option>UPS</option>
            <option>USPS</option>
            <option>FedEx</option>
            <option>OnTrac</option>
            <option>Other</option>
          </select>
          <select className="bd-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`registry-status-${item.registry_id}`}>
            <option value="pending">Pending</option>
            <option value="ordered">Ordered</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button className="bd-btn" disabled={busy} onClick={saveTracking} data-testid={`registry-tracking-save-${item.registry_id}`}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : "Save"}
          </button>
        </div>
      )}
    </li>
  );
}

function SubList({ event, kind, onAdd, onRemove }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [sugOpen, setSugOpen] = useState(true);
  const [err, setErr] = useState("");

  const aiKind = useMemo(() => {
    if (kind.listKey === "likes") return "likes";
    if (kind.listKey === "places") return "places";
    if (kind.listKey.startsWith("todos")) return "todos";
    return "gifts";
  }, [kind]);

  const getSuggestions = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/ai/suggest", {
        event_title: event.title,
        event_type: event.event_type,
        person_name: event.person_name || "",
        likes: event.likes || [],
        kind: aiKind,
      });
      setSuggestions(data.suggestions || []);
      setSugOpen(true);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const items = event[kind.listKey] || [];

  return (
    <div className="bd-card" data-testid={`sub-${kind.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 font-bold">
          {kind.emoji} <span>{kind.title}</span>
        </div>
        <button
          onClick={getSuggestions}
          className="bd-btn bd-btn-ghost"
          style={{ padding: "0.3rem 0.5rem", fontSize: "0.75rem" }}
          disabled={busy}
          data-testid={`sub-ai-${kind.id}`}
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <><Sparkles size={12} /> Suggestions</>}
        </button>
      </div>

      <ul className="space-y-1.5 mb-3">
        {items.length === 0 && (
          <li className="text-sm" style={{ color: "var(--text-dim)" }}>Empty — add one or get suggestions.</li>
        )}
        {items.map((it, i) => (
          <li key={`${kind.id}-${it}-${i}`} className="flex items-center gap-2 group" data-testid={`sub-item-${kind.id}-${i}`}>
            <span className="bd-cal-dot" style={{ background: "var(--primary)" }} />
            <span className="flex-1 text-sm">{it}</span>
            <button
              onClick={() => onRemove(kind.listKey, i)}
              className="opacity-0 group-hover:opacity-100 transition"
              style={{ color: "var(--text-dim)" }}
              data-testid={`sub-remove-${kind.id}-${i}`}
              aria-label="Remove"
            >
              <X size={14} />
            </button>
          </li>
        ))}
      </ul>

      {suggestions.length > 0 && (
        <div
          className="mb-3 bd-ai-shimmer rounded-lg"
          style={{ background: "var(--bg-soft)", padding: sugOpen ? "0.5rem 0.6rem" : "0.3rem 0.6rem" }}
          data-testid={`sub-suggest-panel-${kind.id}`}
        >
          <div className="flex items-center justify-between gap-2 mb-1">
            <button
              type="button"
              onClick={() => setSugOpen((o) => !o)}
              className="flex items-center gap-1.5 text-xs uppercase tracking-widest"
              style={{ color: "var(--text-dim)", background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
              data-testid={`sub-suggest-toggle-${kind.id}`}
              aria-expanded={sugOpen}
            >
              <Sparkles size={11} />
              <span>{suggestions.length} suggestion{suggestions.length === 1 ? "" : "s"}</span>
              <span style={{ display: "inline-block", transform: sugOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 160ms ease" }}>›</span>
            </button>
            <button
              type="button"
              onClick={() => setSuggestions([])}
              className="bd-btn-ghost"
              style={{ padding: "0.15rem 0.3rem", fontSize: "0.7rem", color: "var(--text-dim)", background: "transparent", border: "none", cursor: "pointer" }}
              aria-label="Dismiss suggestions"
              data-testid={`sub-suggest-dismiss-${kind.id}`}
            >
              <X size={12} />
            </button>
          </div>
          {sugOpen && (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 gap-1.5"
              style={{ paddingTop: "0.25rem" }}
              data-testid={`sub-suggest-grid-${kind.id}`}
            >
              {suggestions.map((s, i) => (
                <button
                  key={`sug-${s}-${i}`}
                  onClick={() => {
                    // Populate the input so the user can edit before pressing Add.
                    // Chip removes itself from the list so the panel auto-clears
                    // and shows the "More ideas" refresh once empty.
                    setValue(s);
                    setSuggestions(suggestions.filter((_, j) => j !== i));
                  }}
                  className="bd-chip hover:opacity-80 text-left"
                  style={{ fontSize: "0.7rem", padding: "0.35rem 0.55rem" }}
                  data-testid={`ai-suggest-${kind.id}-${i}`}
                  title="Tap to put this in the box below — then tweak and press Add"
                >
                  <Plus size={10} /> <span className="truncate">{s}</span>
                </button>
              ))}
            </div>
          )}
          {sugOpen && suggestions.length === 0 && (
            <button
              type="button"
              onClick={getSuggestions}
              disabled={busy}
              className="bd-btn bd-btn-ghost mt-2 w-full text-xs"
              style={{ padding: "0.35rem" }}
              data-testid={`sub-suggest-more-${kind.id}`}
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <><Sparkles size={12} /> More ideas</>}
            </button>
          )}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); onAdd(kind.listKey, value); setValue(""); }} className="flex gap-2">
        <input
          className="bd-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Add…"
          data-testid={`sub-input-${kind.id}`}
        />
        <button className="bd-btn" type="submit" style={{ padding: "0.5rem 0.75rem" }} data-testid={`sub-add-${kind.id}`}>
          <Plus size={14} />
        </button>
      </form>
      {err && <p className="text-xs mt-2" style={{ color: "#ff3b30" }}>{err}</p>}
    </div>
  );
}

function LocationBlock({ location }) {
  const q = encodeURIComponent(location);
  const osmUrl = `https://www.openstreetmap.org/search?query=${q}`;
  const googleEmbed = `https://www.google.com/maps?q=${q}&output=embed`;
  return (
    <div className="bd-surface p-5" data-testid="location-block">
      <div className="flex items-center gap-2 mb-3 font-bold">
        <MapPin size={16} /> Where it&apos;s happening
      </div>
      <div className="overflow-hidden" style={{ borderRadius: "var(--radius-card)", border: "1px solid var(--border)" }}>
        <iframe
          title="Event map"
          src={googleEmbed}
          width="100%"
          height="280"
          style={{ border: 0 }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
      <a href={osmUrl} target="_blank" rel="noreferrer" className="text-xs mt-2 inline-block" style={{ color: "var(--text-dim)" }} data-testid="location-osm-link">
        Open in OpenStreetMap →
      </a>
    </div>
  );
}

function ExpenseBlock({ event, total, onChanged }) {
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const add = async (e) => {
    e.preventDefault();
    if (!label.trim() || !amount) return;
    await api.post(`/events/${event.event_id}/expenses`, { label, amount: parseFloat(amount) });
    setLabel(""); setAmount("");
    onChanged();
  };
  const del = async (xid) => {
    await api.delete(`/expenses/${xid}`);
    onChanged();
  };
  const expenses = event.expenses || [];
  return (
    <div className="bd-surface p-5" data-testid="expenses-block">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 font-bold text-lg">
          <Wallet size={18} /> Expenses
        </div>
        <div className="bd-mono text-xl font-bold" data-testid="expenses-total">${total.toFixed(2)}</div>
      </div>
      <ul className="space-y-2 mb-4">
        {expenses.length === 0 && <li style={{ color: "var(--text-dim)" }} className="text-sm">No expenses yet.</li>}
        {expenses.map((x) => (
          <li key={x.expense_id} className="flex items-center justify-between gap-2" data-testid={`expense-${x.expense_id}`}>
            <span className="flex-1 truncate">{x.label}</span>
            <span className="bd-mono">${(x.amount || 0).toFixed(2)}</span>
            <button onClick={() => del(x.expense_id)} className="bd-btn bd-btn-ghost" style={{ padding: "0.3rem 0.5rem" }}>
              <Trash2 size={12} />
            </button>
          </li>
        ))}
      </ul>
      <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-2">
        <input className="bd-input" placeholder="What was it?" value={label} onChange={(e) => setLabel(e.target.value)} required data-testid="expense-label" />
        <input className="bd-input bd-mono" type="number" min="0.01" step="0.01" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid="expense-amount" />
        <button className="bd-btn" type="submit" data-testid="expense-add"><Plus size={14} /> Add</button>
      </form>
    </div>
  );
}

function FundraiserBlock({ event, onRefresh }) {
  const [amount, setAmount] = useState(25);
  const [coupon, setCoupon] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const goal = event.fundraiser_goal || 0;
  const raised = event.fundraiser_raised || 0;
  const pct = goal > 0 ? Math.min(100, (raised / goal) * 100) : 0;
  const donate = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/donations/checkout", {
        event_id: event.event_id,
        amount: parseFloat(amount),
        origin_url: window.location.origin,
        coupon_code: coupon.trim().toUpperCase() || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(formatError(e));
      setBusy(false);
    }
  };
  return (
    <div className="bd-surface p-5" data-testid="fundraiser-block">
      <div className="flex items-center gap-2 mb-3 font-bold text-lg">
        <HeartHandshake size={18} /> Fundraiser
      </div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-dim)" }}>Raised so far</div>
          <div className="bd-mono text-3xl font-bold" data-testid="fundraiser-raised">${raised.toFixed(2)}</div>
        </div>
        {goal > 0 && (
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-dim)" }}>Goal</div>
            <div className="bd-mono text-xl">${goal.toFixed(2)}</div>
          </div>
        )}
      </div>
      {goal > 0 && (
        <div className="bd-progress mb-4">
          <div className="bd-progress-fill" style={{ width: `${pct}%` }} />
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-[120px_140px_1fr] gap-2 items-center">
        <input className="bd-input bd-mono" type="number" min="1" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="donation-amount" />
        <input
          className="bd-input bd-mono uppercase"
          placeholder="Coupon"
          value={coupon}
          onChange={(e) => setCoupon(e.target.value.toUpperCase())}
          data-testid="donation-coupon"
        />
        <button className="bd-btn" onClick={donate} disabled={busy} data-testid="donation-checkout">
          {busy ? <Loader2 size={14} className="animate-spin" /> : `Donate $${parseFloat(amount || 0).toFixed(2)} with Stripe`}
        </button>
      </div>
      {err && <p className="text-xs mt-2" style={{ color: "#ff3b30" }} data-testid="fundraiser-error">{err}</p>}
      <p className="text-xs mt-2" style={{ color: "var(--text-dim)" }}>
        Stripe test mode. Use card <span className="bd-mono">4242 4242 4242 4242</span>, any future expiry, any CVC.
      </p>
    </div>
  );
}
